import { createDriver } from "use-neo4j";
import { createNotificationThunk } from "../page/PageThunks";
import { updateDashboardSetting } from "../settings/SettingsActions";
import {
  addGenieList,
  addPage,
  addPageWithDetails,
  deleteGenieList,
  movePage,
  removePage,
  resetDashboardState,
  setDashboard,
  setGenieLists,
  updateGenieList,
  updateGenieListStatus,
} from "./DashboardActions";
import { QueryStatus, runCypherQuery } from "../report/ReportQueryRunner";
import {
  setParametersToLoadAfterConnecting,
  setWelcomeScreenOpen,
} from "../application/ApplicationActions";
import { updateGlobalParametersThunk } from "../settings/SettingsThunks";
import { getActiveUserName } from "../utils/getUsername";
import { GENIELIST_DATABASE } from "../constants";

function createUUID() {
  var dt = new Date().getTime();
  var uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (c) {
      var r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c == "x" ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return uuid;
}

export const removePageThunk = (number) => (dispatch: any, getState: any) => {
  try {
    const numberOfPages = getState().dashboard.pages.length;

    if (numberOfPages == 1) {
      dispatch(
        createNotificationThunk(
          "Cannot remove page",
          "You can't remove the only page of a dashboard."
        )
      );
      return;
    }
    if (number >= numberOfPages - 1) {
      dispatch(
        updateDashboardSetting("pagenumber", Math.max(0, numberOfPages - 2))
      );
    }
    dispatch(removePage(number));
  } catch (e) {
    dispatch(createNotificationThunk("Unable to remove page", e));
  }
};

export const addPageThunk = () => (dispatch: any, getState: any) => {
  try {
    const numberOfPages = getState().dashboard.pages.length;
    dispatch(addPage());
    dispatch(updateDashboardSetting("pagenumber", numberOfPages));
  } catch (e) {
    dispatch(createNotificationThunk("Unable to create page", e));
  }
};

export const addPageWithDetailsThunk =
  (data) => (dispatch: any, getState: any) => {
    try {
      const numberOfPages = getState().dashboard.pages.length;
      dispatch(addPageWithDetails(data));
      dispatch(updateDashboardSetting("pagenumber", numberOfPages));
    } catch (e) {
      dispatch(createNotificationThunk("Unable to create page", e));
    }
  };

export const movePageThunk =
  (oldIndex: number, newIndex: number) => (dispatch: any, getState: any) => {
    try {
      if (getState().dashboard.settings.pagenumber == oldIndex) {
        dispatch(updateDashboardSetting("pagenumber", newIndex));
      }
      dispatch(movePage(oldIndex, newIndex));
    } catch (e) {
      dispatch(createNotificationThunk("Unable to move page", e));
    }
  };

export const loadDashboardThunk = (text) => (dispatch: any, getState: any) => {
  try {
    if (text.length == 0) {
      throw "No dashboard file specified. Did you select a file?";
    }
    if (text.trim() == "{}") {
      dispatch(resetDashboardState());
      return;
    }
    const dashboard = JSON.parse(text);

    // Attempt upgrade if dashboard version is outdated.
    if (dashboard["version"] == "1.1") {
      const upgradedDashboard = upgradeDashboardVersion(
        dashboard,
        "1.1",
        "2.0"
      );
      dispatch(setDashboard(upgradedDashboard));
      dispatch(setWelcomeScreenOpen(false));
      dispatch(
        createNotificationThunk(
          "Successfully upgraded dashboard",
          "Your old dashboard was migrated to version 2.0. You might need to refresh this page."
        )
      );

      return;
    }
    if (dashboard["version"] == "2.0") {
      const upgradedDashboard = upgradeDashboardVersion(
        dashboard,
        "2.0",
        "2.1"
      );
      dispatch(setDashboard(upgradedDashboard));
      dispatch(setWelcomeScreenOpen(false));
      dispatch(
        createNotificationThunk(
          "Successfully upgraded dashboard",
          "Your old dashboard was migrated to version 2.1. You might need to refresh this page."
        )
      );

      return;
    }
    if (dashboard["version"] != "2.1") {
      throw (
        "Invalid dashboard version: " +
        dashboard.version +
        ". Try restarting the application, or retrieve your cached dashboard using a debug report."
      );
    }
    dispatch(getAllGenieListsToUserName());
    // Reverse engineer the minimal set of fields from the selection loaded.
    dashboard.pages.forEach((p) => {
      p.reports.forEach((r) => {
        if (r.selection) {
          r["fields"] = [];
          Object.keys(r.selection).forEach((f) => {
            r["fields"].push([f, r.selection[f]]);
          });
        }
      });
    });
    dispatch(setDashboard(dashboard));
    const application = getState().application;
    dispatch(
      updateGlobalParametersThunk(application.parametersToLoadAfterConnecting)
    );
    dispatch(setParametersToLoadAfterConnecting(null));
    dispatch(getAllGenieListsToUserName());
  } catch (e) {
    dispatch(createNotificationThunk("Unable to load dashboard", e));
  }
};

export const saveDashboardToNeo4jThunk =
  (driver, database, dashboard, date, user, overwrite = false) =>
  (dispatch: any, getState: any) => {
    try {
      const uuid = createUUID();
      const title = dashboard.title;
      const version = dashboard.version;
      const content = dashboard;

      // Generate a cypher query to save the dashboard.
      const query = overwrite
        ? "OPTIONAL MATCH (n:_Neodash_Dashboard{title:$title}) DELETE n WITH 1 as X LIMIT 1 CREATE (n:_Neodash_Dashboard) SET n.uuid = $uuid, n.title = $title, n.version = $version, n.user = $user, n.content = $content, n.date = datetime($date) RETURN $uuid as uuid"
        : "CREATE (n:_Neodash_Dashboard) SET n.uuid = $uuid, n.title = $title, n.version = $version, n.user = $user, n.content = $content, n.date = datetime($date) RETURN $uuid as uuid";
      runCypherQuery(
        driver,
        database,
        query,
        {
          uuid: uuid,
          title: title,
          version: version,
          user: user,
          content: JSON.stringify(dashboard, null, 2),
          date: date,
        },
        {},
        ["uuid"],
        1,
        () => {
          return;
        },
        (records) => {
          if (
            records &&
            records[0] &&
            records[0]["_fields"] &&
            records[0]["_fields"][0] &&
            records[0]["_fields"][0] == uuid
          ) {
            dispatch(
              createNotificationThunk(
                "🎉 Success!",
                "Your current dashboard was saved to Neo4j."
              )
            );
          } else {
            dispatch(
              createNotificationThunk(
                "Unable to save dashboard",
                "Do you have write access to the '" + database + "' database?"
              )
            );
          }
        }
      );
    } catch (e) {
      dispatch(createNotificationThunk("Unable to save dashboard to Neo4j", e));
    }
  };

export const loadDashboardFromNeo4jByUUIDThunk =
  (driver, database, uuid, callback) => (dispatch: any, getState: any) => {
    try {
      runCypherQuery(
        driver,
        database,
        "MATCH (n:_Neodash_Dashboard) WHERE n.uuid = $uuid RETURN n.content as dashboard",
        { uuid: uuid },
        {},
        ["dashboard"],
        1,
        () => {
          return;
        },
        (records) => {
          if (records.length == 0) {
            dispatch(
              createNotificationThunk(
                "Unable to load dashboard.",
                "A dashboard with the provided UUID could not be found."
              )
            );
          }
          callback(records[0]["_fields"][0]);
        }
      );
    } catch (e) {
      dispatch(createNotificationThunk("Unable to load dashboard to Neo4j", e));
    }
  };

export const loadDashboardFromNeo4jByNameThunk =
  (driver, database, name, callback) => (dispatch: any, getState: any) => {
    try {
      runCypherQuery(
        driver,
        database,
        "MATCH (d:_Neodash_Dashboard) WHERE d.title = $name RETURN d.content as dashboard ORDER by d.date DESC LIMIT 1",
        { name: name },
        {},
        ["dashboard"],
        1,
        (data) => {
          return;
        },
        (records) => {
          if (records.length == 0) {
            dispatch(
              createNotificationThunk(
                "Unable to load dashboard.",
                "A dashboard with the provided name could not be found."
              )
            );
          }
          callback(records[0]["_fields"][0]);
        }
      );
    } catch (e) {
      dispatch(
        createNotificationThunk("Unable to load dashboard from Neo4j", e)
      );
    }
  };

export const loadDashboardListFromNeo4jThunk =
  (driver, database, callback) => (dispatch: any, getState: any) => {
    try {
      runCypherQuery(
        driver,
        database,
        "MATCH (n:_Neodash_Dashboard) RETURN n.uuid as id, n.title as title, toString(n.date) as date,  n.user as author, n.version as version ORDER BY date DESC",
        {},
        {},
        ["id, title, date, user, version"],
        1000,
        () => {
          return;
        },
        (records) => {
          if (!records || !records[0] || !records[0]["_fields"]) {
            callback([]);
            return;
          }
          const result = records.map((r) => {
            return {
              id: r["_fields"][0],
              title: r["_fields"][1],
              date: r["_fields"][2],
              author: r["_fields"][3],
              version: r["_fields"][4],
            };
          });
          callback(result);
        }
      );
    } catch (e) {
      dispatch(
        createNotificationThunk("Unable to load dashboard list from Neo4j", e)
      );
    }
  };

export const loadDatabaseListFromNeo4jThunk =
  (driver, callback) => (dispatch: any, getState: any) => {
    try {
      runCypherQuery(
        driver,
        "system",
        "SHOW DATABASES yield name RETURN name",
        {},
        {},
        ["name"],
        1000,
        () => {
          return;
        },
        (records) => {
          const result = records.map((r) => {
            return r["_fields"] && r["_fields"][0];
          });
          callback(result);
        }
      );
    } catch (e) {
      dispatch(
        createNotificationThunk("Unable to list databases from Neo4j", e)
      );
    }
  };

export function upgradeDashboardVersion(
  dashboard: any,
  origin: string,
  target: string
) {
  if (origin == "2.0" && target == "2.1") {
    dashboard["pages"].forEach((p, i) => {
      // From v2.1 onwards, reports will have their x,y positions explicitly specified.
      // v2.0 dashboards do not have this, therefore we must assign them.
      // Additionally we divide the old report height by 1.5 (adjusted vertical scaling factor).

      var xPos = 0;
      var yPos = 0;
      var rowHeight = 1;
      p["reports"].forEach((r, j) => {
        const reportWidth = parseInt(r["width"]);
        const reportHeight = parseInt(r["height"]);
        dashboard["pages"][i]["reports"][j] = {
          x: xPos,
          y: yPos,
          ...dashboard["pages"][i]["reports"][j],
        };
        dashboard["pages"][i]["reports"][j]["height"] = reportHeight / 1.5;
        xPos += reportWidth;
        rowHeight = Math.max(reportHeight / 1.5, rowHeight);
        if (xPos >= 12) {
          xPos = 0;
          yPos += rowHeight;
          rowHeight = 1;
        }
      });
    });
    dashboard["version"] = "2.1";
    return dashboard;
  }
  if (origin == "1.1" && target == "2.0") {
    const upgradedDashboard = {};
    upgradedDashboard["title"] = dashboard["title"];
    upgradedDashboard["version"] = "2.0";
    upgradedDashboard["settings"] = {
      pagenumber: dashboard["pagenumber"],
      editable: dashboard["editable"],
    };
    const upgradedDashboardPages = [];
    dashboard["pages"].forEach((p) => {
      const newPage = {};
      newPage["title"] = p["title"];
      const newPageReports = [];
      p["reports"].forEach((r) => {
        // only migrate value report types.
        if (
          [
            "table",
            "graph",
            "bar",
            "line",
            "map",
            "value",
            "json",
            "select",
            "iframe",
            "text",
          ].indexOf(r["type"]) == -1
        ) {
          return;
        }
        if (r["type"] == "select") {
          r["query"] = "";
        }
        const newPageReport = {
          title: r["title"],
          width: r["width"],
          height: r["height"] * 0.75,
          type: r["type"],
          parameters: r["parameters"],
          query: r["query"],
          selection: {},
          settings: {},
        };

        newPageReports.push(newPageReport);
      });
      newPage["reports"] = newPageReports;
      upgradedDashboardPages.push(newPage);
    });
    upgradedDashboard["pages"] = upgradedDashboardPages;
    return upgradedDashboard;
  }
  throw new Error("Invalid upgrade path: " + origin + " --> " + target);
}

export const setActiveGenieListsFalseThunk =
  (driver, database, userName, dashboardName, callback) =>
  (dispatch: any, getState: any) => {
    try {
      // Generate a cypher query to save the dashboard.
      const query =
        "MATCH (n:GeneLists{user_name:$userName,dashboard_name:$dashboardName}) SET n.active_genelist = FALSE RETURN n";
      runCypherQuery(
        driver,
        database,
        query,
        {
          userName: userName,
          dashboardName: dashboardName,
        },
        {},
        [],
        100,
        () => {
          return;
        },
        (records) => {
          callback(records);
        }
      );
    } catch (e) {
      dispatch(createNotificationThunk("Unable to save dashboard to Neo4j", e));
    }
  };

export const saveSelectedGenieListThunk =
  (
    driver,
    database,
    userName,
    genieList,
    geneCount,
    genieListName,
    dashboardName
  ) =>
  (dispatch: any, getState: any) => {
    try {
      // Generate a cypher query to save the dashboard.
      const query =
        "CREATE p = (n:GeneLists{user_name:$userName,active_genelist:False,genelist:$genieList,gene_count:$geneCount,genelist_name:$genieListName,dashboard_name:$dashboardName,database:$database}) RETURN apoc.node.id(n) as nodeId ,p";
      runCypherQuery(
        driver,
        GENIELIST_DATABASE,
        query,
        {
          userName: userName,
          genieList: genieList,
          geneCount: geneCount,
          genieListName: genieListName,
          dashboardName: dashboardName,
          database: database,
        },
        {},
        [],
        1,
        () => {
          return;
        },
        (record) => {
          if (
            record?.length &&
            record[0]["_fields"] &&
            record[0]["_fields"].length === 2
          ) {
            const savedGenieList = {
              id: record[0]["_fields"][0].low,
              ...record[0]["_fields"][1].end.properties,
            };
            dispatch(addGenieList(savedGenieList));

            dispatch(
              createNotificationThunk(
                "🎉 Success!",
                "Your genie list saved succesfully."
              )
            );
          } else {
            dispatch(
              createNotificationThunk(
                "Unable to save Genie List",
                "Do you have write access to the '" + database + "' database?"
              )
            );
          }
        }
      );
    } catch (e) {
      dispatch(createNotificationThunk("Unable to save dashboard to Neo4j", e));
    }
  };

export const getAllGenieListsToUserName =
  () => (dispatch: any, getState: any) => {
    const connectionParams = getState()?.application?.connection;
    const driver = createDriver(
      connectionParams?.protocol,
      connectionParams?.url,
      connectionParams?.port,
      connectionParams?.username,
      connectionParams?.password
    );

    if (!connectionParams?.database) {
      return;
    }
    try {
      // Generate a cypher query to save the dashboard.
      const query =
        "MATCH (n:GeneLists) WHERE n.user_name = $userName and n.dashboard_name = $dashboardName and n.database = $database return apoc.node.id(n) as nodeId,n";
      runCypherQuery(
        driver,
        GENIELIST_DATABASE,
        query,
        {
          userName: connectionParams?.username,
          dashboardName: getState().dashboard?.title,
          database: connectionParams?.database,
        },
        {},
        [],
        100,
        (status) => {
          if (status === QueryStatus.NO_DATA) {
            dispatch(setGenieLists([]));
          }
        },
        (records) => {
          const userGenieLists: any = [];
          records.forEach((record) => {
            if (record && record["_fields"] && record["_fields"].length === 2) {
              const savedGenieList = {
                id: record["_fields"][0].low,
                ...record["_fields"][1].properties,
              };
              if (savedGenieList?.active_genelist) {
                dispatch(
                  updateGlobalParametersThunk({
                    neodash_genelist_name: savedGenieList?.genelist?.split(","),
                  })
                );
              }
              userGenieLists.push(savedGenieList);
            }
          });
          dispatch(setGenieLists(userGenieLists));
        }
      );
    } catch (e) {
      dispatch(createNotificationThunk("Unable to save dashboard to Neo4j", e));
    }
  };

export const getAllGenieListsToUserNameWithConnectionProps =
  (driver, database) => (dispatch: any, getState: any) => {
    if (!getState().dashboard?.title) {
      dispatch(setGenieLists([]));
      return;
    }

    try {
      // Generate a cypher query to save the dashboard.

      const query =
        "MATCH (n:GeneLists) WHERE n.user_name = $userName and n.dashboard_name = $dashboardName and n.database = $database return apoc.node.id(n) as nodeId,n";
      runCypherQuery(
        driver,
        GENIELIST_DATABASE,
        query,
        {
          userName: getActiveUserName(
            getState()?.application?.connection?.username
          ),
          dashboardName: getState().dashboard?.title,
          database: database,
        },
        {},
        [],
        100,
        (status) => {},
        (records) => {
          const userGenieLists: any = [];
          records.forEach((record) => {
            if (record && record["_fields"] && record["_fields"].length === 2) {
              const savedGenieList = {
                id: record["_fields"][0].low,
                ...record["_fields"][1].properties,
              };
              if (savedGenieList?.active_genelist) {
                dispatch(
                  updateGlobalParametersThunk({
                    neodash_genelist_name: savedGenieList?.genelist?.split(","),
                  })
                );
              }
              userGenieLists.push(savedGenieList);
            }
          });
          dispatch(setGenieLists(userGenieLists));
        }
      );
    } catch (e) {
      dispatch(createNotificationThunk("Unable to save dashboard to Neo4j", e));
    }
  };

export const changeActiveGenieListThunk =
  (driver, database, id, condition, callback = null) =>
  (dispatch: any, getState: any) => {
    try {
      // Generate a cypher query to save the dashboard.
      const query =
        "MATCH (n:GeneLists) WHERE apoc.node.id(n)=$id SET n.active_genelist=$condition return n";
      runCypherQuery(
        driver,
        GENIELIST_DATABASE,
        query,
        {
          id: id,
          condition: condition,
        },
        {},
        [],
        1,
        () => {
          return;
        },
        (record) => {
          if (
            record?.length &&
            record[0]["_fields"] &&
            record[0]["_fields"].length
          ) {
            const updatedList = {
              id: record[0]["_fields"][0].identity.low,
              ...record[0]["_fields"][0].properties,
            };
            dispatch(updateGenieListStatus(updatedList));
            if (callback !== null) {
              callback(updatedList);
            }
          } else {
            dispatch(
              createNotificationThunk(
                "Unable to save Genie List",
                "Do you have write access to the '" + database + "' database?"
              )
            );
          }
        }
      );
    } catch (e) {
      dispatch(createNotificationThunk("Unable to save dashboard to Neo4j", e));
    }
  };

export const deleteGenieListThunk =
  (driver, database, id) => (dispatch: any, getState: any) => {
    try {
      // Generate a cypher query to save the dashboard.
      const query =
        "Match p = (n:GeneLists) WHERE  apoc.node.id(n) = $id delete p return p";
      runCypherQuery(
        driver,
        GENIELIST_DATABASE,
        query,
        {
          id: id,
        },
        {},
        [],
        1,
        (status) => {
          return;
        },
        (record) => {
          if (
            record?.length &&
            record[0]["_fields"] &&
            record[0]["_fields"].length
          ) {
            dispatch(deleteGenieList(id));
          } else {
            dispatch(
              createNotificationThunk(
                "Error occured while deleting the GenieList",
                null
              )
            );
          }
        }
      );
    } catch (e) {
      dispatch(createNotificationThunk("Unable to save dashboard to Neo4j", e));
    }
  };

export const updateGenieListNameThunk =
  (driver, database, id, name) => (dispatch: any, getState: any) => {
    console.log("This function called");
    try {
      // Generate a cypher query to save the dashboard.
      const query =
        "MATCH (n:GeneLists) WHERE apoc.node.id(n)=$id SET n.genelist_name=$name return n";
      runCypherQuery(
        driver,
        GENIELIST_DATABASE,
        query,
        {
          id: id,
          name: name,
        },
        {},
        [],
        1,
        () => {
          return;
        },
        (record) => {
          if (
            record?.length &&
            record[0]["_fields"] &&
            record[0]["_fields"].length
          ) {
            const updatedList = {
              id: record[0]["_fields"][0].identity.low,
              ...record[0]["_fields"][0].properties,
            };

            dispatch(updateGenieList(updatedList));
          } else {
            dispatch(
              createNotificationThunk(
                "Unable to save Genie List",
                "Do you have write access to the '" + database + "' database?"
              )
            );
          }
        }
      );
    } catch (e) {
      dispatch(createNotificationThunk("Unable to save dashboard to Neo4j", e));
    }
  };

export const updateGenieListThunk =
  (driver, database, id, geneList, geneCount) =>
  (dispatch: any, getState: any) => {
    try {
      // Generate a cypher query to save the dashboard.
      const query =
        "MATCH (n:GeneLists) WHERE apoc.node.id(n)=$id SET n.genelist=$geneList, n.gene_count=$geneCount return n";
      runCypherQuery(
        driver,
        GENIELIST_DATABASE,
        query,
        {
          id: id,
          geneList: geneList,
          geneCount: geneCount,
        },
        {},
        [],
        1,
        (status) => {
          return;
        },
        (record) => {
          if (
            record?.length &&
            record[0]["_fields"] &&
            record[0]["_fields"].length
          ) {
            const updatedList = {
              id: record[0]["_fields"][0].identity.low,
              ...record[0]["_fields"][0].properties,
            };
            dispatch(
              updateGlobalParametersThunk({
                neodash_genelist_name: updatedList.genelist.split(","),
              })
            );
            dispatch(updateGenieList(updatedList));
          } else {
            dispatch(
              createNotificationThunk(
                "Unable to update Genie List",
                "Do you have write access to the '" + database + "' database?"
              )
            );
          }
        }
      );
    } catch (e) {
      dispatch(createNotificationThunk("Unable to save dashboard to Neo4j", e));
    }
  };

export const searchGeneByTextThunk =
  (driver, database, searchText, callback) =>
  (dispatch: any, getState: any) => {
    try {
      // Generate a cypher query to save the dashboard.
      const query =
        "match (n:Gene) where n.description contains $description return n.name,n.description limit 10 union " +
        "match (n:Gene) where n.name contains $name return n.name,n.description limit 10 union " +
        "match (n:Gene) where n.chr contains $chr return n.name,n.description  limit 10";
      runCypherQuery(
        driver,
        database,
        query,
        {
          description: searchText,
          name: searchText,
          chr: searchText,
        },
        {},
        [],
        10,
        (status) => {
          return;
        },
        (records) => {
          if (records?.length) {
            const geneList = records.map((record) => {
              return {
                name: record["_fields"][0],
                description: record["_fields"][1],
                label: record["_fields"][0],
              };
            });
            callback(geneList);
          }
        }
      );
    } catch (e) {
      dispatch(createNotificationThunk("Unable to save dashboard to Neo4j", e));
    }
  };
